import { ExecutionLinkAnswer } from '../ExecutionLinkAnswer'
import { ExecutionFileAnswer } from '../ExecutionFileAnswer'
import { ExecutionPollAnswer } from '../ExecutionPollAnswer'
import React from 'react'
import { Button } from 'components/ui'
import { Minus, Plus } from 'lucide-react'
import { Subtask, Result } from 'constants/interfaces'
import keys from 'constants/keys'
import colors from 'constants/colors'
import { RadioCheckbox } from 'components/TasksV2/UI/RadioCheckbox'
import { useTranslateDefaultTitle } from 'components/TasksV2/hooks/useTranslateDefaultTitle'
import { AnswerDraft } from '../../model/execution'
import { useWorkspaceText } from '../../hooks/useWorkspaceText'
import styles from './styles.module.css'

export function ExecutionAnswer({
  subtask,
  draft,
  onChange,
  disabled,
  result,
}: {
  subtask: Subtask
  draft: AnswerDraft
  onChange: (patch: Partial<AnswerDraft>) => void
  disabled: boolean
  result?: Result
}) {
  const t = useWorkspaceText()
  const questionTitle = useTranslateDefaultTitle(subtask.title)
  const questionId = `execution-question-${subtask.st_id}`
  if (subtask.type === keys.SUBTASK_TYPES.LINK_SUBTASK)
    return <ExecutionLinkAnswer subtask={subtask} draft={draft} onChange={onChange} disabled={disabled} />
  if (subtask.type === keys.SUBTASK_TYPES.UPLOAD_FILE_SUBTASK)
    return <ExecutionFileAnswer subtask={subtask} result={result} disabled={disabled} />
  if (subtask.type === keys.SUBTASK_TYPES.POLL_SUBTASK)
    return <ExecutionPollAnswer subtask={subtask} draft={draft} onChange={onChange} disabled={disabled} />
  if (subtask.type === keys.SUBTASK_TYPES.PRIVATE_POLL)
    return (
      <fieldset
        className={styles.options}
        disabled={disabled}
        style={{ '--quiz-color': colors.privatePoll } as React.CSSProperties}
      >
        <legend>
          {t.answer} <small>{subtask.is_multi_choice ? t.multiple : t.single}</small>
        </legend>
        {(subtask.options || []).map((option, index) => (
          <div key={index} className={styles.option}>
            <RadioCheckbox
              value={option}
              fillColor={colors.privatePoll}
              disabled={disabled}
              inputType={subtask.is_multi_choice ? 'checkbox' : 'radio'}
              name={`answer-${subtask.st_id}`}
              isChecked={draft.votes?.includes(index) || false}
              onChange={() =>
                onChange({
                  votes: subtask.is_multi_choice
                    ? draft.votes?.includes(index)
                      ? draft.votes.filter((value) => value !== index)
                      : [...(draft.votes || []), index]
                    : [index],
                })
              }
            >
              <span className={styles.optionLabel} dir="auto">
                {option}
              </span>
            </RadioCheckbox>
          </div>
        ))}
      </fieldset>
    )
  if (subtask.type === keys.SUBTASK_TYPES.OPEN_QUESTION)
    return (
      <div className={styles.question}>
        {questionTitle && (
          <h2 id={questionId} className={styles.questionTitle} dir="auto">
            {questionTitle}
          </h2>
        )}
        {t.answer}
        {subtask.is_numeric ? (
          <span className={styles.number}>
            <Button
              variant="icon"
              border="1px solid #dde2e6"
              borderRadius="10px"
              bg="white"
              p="12px"
              h="44px"
              minW="44px"
              type="button"
              aria-label={t.decrease}
              isDisabled={disabled}
              onClick={() => onChange({ answer: String((Number(draft.answer) || 0) - 1) })}
            >
              <Minus size={18} />
            </Button>
            <input
              aria-label={t.answer}
              aria-describedby={questionTitle ? questionId : undefined}
              type="number"
              step="any"
              value={draft.answer || ''}
              onChange={(event) => onChange({ answer: event.target.value })}
              disabled={disabled}
            />
            <Button
              variant="icon"
              border="1px solid #dde2e6"
              borderRadius="10px"
              bg="white"
              p="12px"
              h="44px"
              minW="44px"
              type="button"
              aria-label={t.increase}
              isDisabled={disabled}
              onClick={() => onChange({ answer: String((Number(draft.answer) || 0) + 1) })}
            >
              <Plus size={18} />
            </Button>
          </span>
        ) : (
          <textarea
            aria-label={t.answer}
            aria-describedby={questionTitle ? questionId : undefined}
            rows={5}
            value={draft.answer || ''}
            onChange={(event) => onChange({ answer: event.target.value })}
            placeholder={t.writeAnswer}
            disabled={disabled}
          />
        )}
      </div>
    )
  return <p>{t.unsupported}</p>
}
